/**
 * Hash-based router for ClawWars
 *
 * Syncs the in-memory Page state with the URL hash so that:
 * - Clicking Docs updates URL to #docs
 * - Clicking Updates updates URL to #updates
 * - Direct navigation to #docs loads the Docs page
 * - Browser back/forward works
 *
 * Format: #page or #page/param (e.g. #nation/US, #war/42)
 */

export type Page =
  | { type: "landing" }
  | { type: "ios-intro" }
  | { type: "dashboard"; tab?: string }
  | { type: "nation"; code: string }
  | { type: "war"; id: number }
  | { type: "profile" }
  | { type: "create-coin" }
  | { type: "docs" }
  | { type: "admin" }
  | { type: "updates" }
  | { type: "privacy" }
  | { type: "terms" }
  | { type: "not-found" };

const DASHBOARD_TAB_ALIASES: Record<string, string> = {
  diorama: "hq",
  agents: "ops",
};

const DASHBOARD_TAB_LABELS: Record<string, string> = {
  hq: "HQ",
  war_map: "War Map",
  battle_feed: "Battle Feed",
  rankings: "Rankings",
  alliances: "Alliances",
  mercenaries: "Mercenaries",
  market: "Market",
  simulation: "Simulation",
  ops: "Ops",
  intelligence: "Intel",
  create_coin: "Create Coin",
  coliseum: "Coliseum",
  quests: "Quests",
  heroes: "Heroes",
  guild: "Guild",
  campaign: "Campaign",
  shop: "Shop",
  battle_pass: "Battle Pass",
  kvk: "KvK",
  marketplace: "Marketplace",
};

export function normalizeDashboardTab(tab?: string | null): string {
  if (!tab) {
    return "hq";
  }

  const clean = tab.trim().toLowerCase();
  return DASHBOARD_TAB_ALIASES[clean] ?? clean;
}

export function formatDashboardTabLabel(tab?: string | null): string {
  const normalized = normalizeDashboardTab(tab);
  return DASHBOARD_TAB_LABELS[normalized] ?? normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Convert a Page object to a URL hash string */
export function pageToHash(page: Page): string {
  switch (page.type) {
    case "landing":
      return "";
    case "ios-intro":
      return "#command";
    case "dashboard": {
      const tab = normalizeDashboardTab(page.tab);
      if (tab === "hq") return "#hq";
      if (tab === "war_map") return "#map";
      if (tab === "battle_feed") return "#wars";
      if (tab === "intelligence") return "#intel";
      if (tab === "ops") return "#ops";
      return `#dashboard/${tab}`;
    }
    case "nation":
      return `#nation/${page.code}`;
    case "war":
      return `#war/${page.id}`;
    case "profile":
      return "#profile";
    case "create-coin":
      return "#create-coin";
    case "docs":
      return "#docs";
    case "admin":
      return "#admin";
    case "updates":
      return "#updates";
    case "privacy":
      return "#privacy";
    case "terms":
      return "#terms";
    case "not-found":
      return "#not-found";
    default:
      return "";
  }
}

/** Parse a URL hash string into a Page object */
export function hashToPage(hash: string, isAuthed: boolean): Page {
  const clean = hash.replace(/^#\/?/, "").toLowerCase();
  const parts = clean.split("/");
  const route = parts[0];

  switch (route) {
    case "dashboard":
      return isAuthed ? { type: "dashboard", tab: normalizeDashboardTab(parts[1]) } : { type: "landing" };
    case "command":
    case "intro":
      return isAuthed ? { type: "dashboard", tab: "hq" } : { type: "ios-intro" };
    case "hq":
    case "diorama":
      return isAuthed ? { type: "dashboard", tab: "hq" } : { type: "landing" };
    case "map":
      return isAuthed ? { type: "dashboard", tab: "war_map" } : { type: "landing" };
    case "wars":
      return isAuthed ? { type: "dashboard", tab: "battle_feed" } : { type: "landing" };
    case "intel":
      return isAuthed ? { type: "dashboard", tab: "intelligence" } : { type: "landing" };
    case "ops":
    case "agents":
      return isAuthed ? { type: "dashboard", tab: "ops" } : { type: "landing" };
    case "nation":
      return parts[1] ? { type: "nation", code: parts[1].toUpperCase() } : { type: "dashboard" };
    case "war": {
      const id = parseInt(parts[1], 10);
      return !isNaN(id) ? { type: "war", id } : { type: "dashboard" };
    }
    case "profile":
      return isAuthed ? { type: "profile" } : { type: "landing" };
    case "create-coin":
      return isAuthed ? { type: "create-coin" } : { type: "landing" };
    case "docs":
      return { type: "docs" };
    case "admin":
      return isAuthed ? { type: "admin" } : { type: "landing" };
    case "updates":
      return { type: "updates" };
    case "privacy":
      return { type: "privacy" };
    case "terms":
      return { type: "terms" };
    case "":
      return isAuthed ? { type: "dashboard", tab: "hq" } : { type: "landing" };
    default:
      return { type: "not-found" };
  }
}
